/**
 * Sidebar Section Configurations
 *
 * This file contains the configuration for each resource type's sidebar section.
 * It centralizes the logic for icons, empty messages, and other display configurations.
 */

import React, { ReactNode } from "react";
import {
  FaTag,
  FaToggleOn,
  FaToggleOff,
  FaFileImport,
  FaLayerGroup,
  FaExclamationTriangle,
  FaFileAlt,
  FaChartBar,
  FaExchangeAlt,
  FaRobot,
  FaCode,
  FaTh,
  FaProjectDiagram,
  FaCircle,
} from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SourceIcon } from "@/components/resource-card";
import { getEnrichmentIcon } from "@/features/enrichments/utils";
import { getWidgetIcon } from "@/hooks/use-widgets";
import { AlertType } from "@/models/alert";
import { Tracker } from "@/models/tracker";
import { Enrichment } from "@/models/enrichment";
import { Alert } from "@/models/alert";
import { Investigation } from "@/models/investigation";
import { WidgetWithContext } from "@/hooks/use-widgets";
import { Dashboard } from "@/models/dashboard";
import { ResourceType } from "@/models/resource-group";

// =============================================================================
// Types
// =============================================================================

export interface SidebarSectionConfig<T = unknown> {
  /** Resource type identifier */
  resourceType: ResourceType;
  /** Empty state message */
  emptyMessage: string;
  /** Empty state icon */
  emptyIcon?: ReactNode;
  /** Empty state sub-message */
  emptySubMessage?: string;
  /** Search placeholder */
  searchPlaceholder?: string;
  /** No results message */
  noResultsMessage?: string;
  /** Whether this section supports ResourceGroups (folders) */
  supportsFolders: boolean;
  /** Function to get group icon based on groupKey and groupBy */
  getGroupIcon: (groupKey: string, groupBy: string, items: T[]) => ReactNode;
}

// =============================================================================
// Alert Type Icons and Labels
// =============================================================================

const ALERT_TYPE_ICONS: Record<AlertType, ReactNode> = {
  new_documents: <FaFileAlt size={12} className="text-blue-500" />,
  threshold: <FaChartBar size={12} className="text-emerald-500" />,
  change: <FaExchangeAlt size={12} className="text-amber-500" />,
  anomaly: <FaRobot size={12} className="text-purple-500" />,
  custom: <FaCode size={12} className="text-rose-500" />,
};

const ALERT_TYPE_LABELS: Record<AlertType, string> = {
  new_documents: "Nouveaux documents",
  threshold: "Seuil",
  change: "Changement",
  anomaly: "Anomalie",
  custom: "Personnalisé",
};

// =============================================================================
// Tracker Configuration
// =============================================================================

export const trackerSectionConfig: SidebarSectionConfig<Tracker> = {
  resourceType: "tracker",
  emptyMessage: "Aucun tracker",
  searchPlaceholder: "Rechercher...",
  noResultsMessage: "Aucun résultat",
  supportsFolders: true,
  getGroupIcon: (groupKey: string, groupBy: string) => {
    switch (groupBy) {
      case "source":
        return <SourceIcon source={groupKey} size="sm" />;
      case "tags":
        return <FaTag size={12} />;
      case "status":
        return groupKey === "Actifs" ? (
          <FaToggleOn size={12} className="text-green-500" />
        ) : (
          <FaToggleOff size={12} className="text-gray-400" />
        );
      case "type":
        return groupKey === "Import" ? (
          <FaFileImport size={12} />
        ) : (
          <FaLayerGroup size={12} />
        );
      default:
        return <FaLayerGroup size={12} />;
    }
  },
};

// =============================================================================
// Enrichment Configuration
// =============================================================================

export const enrichmentSectionConfig: SidebarSectionConfig<Enrichment> = {
  resourceType: "enrichment",
  emptyMessage: "Aucun enrichissement",
  searchPlaceholder: "Rechercher...",
  noResultsMessage: "Aucun résultat",
  supportsFolders: true,
  getGroupIcon: (groupKey: string, groupBy: string, enrichments: Enrichment[]) => {
    switch (groupBy) {
      case "type":
        const firstEnrichment = enrichments[0];
        if (firstEnrichment?.type) {
          return (
            <FontAwesomeIcon
              icon={getEnrichmentIcon(firstEnrichment.type)}
              className="text-purple-400"
            />
          );
        }
        return <FaLayerGroup size={12} />;
      case "status":
        if (groupKey === "Actifs") {
          return <FaToggleOn size={12} className="text-green-500" />;
        } else if (groupKey === "Inactifs") {
          return <FaToggleOff size={12} className="text-gray-400" />;
        } else if (groupKey === "Erreur") {
          return <FaExclamationTriangle size={12} className="text-red-500" />;
        }
        return <FaLayerGroup size={12} />;
      default:
        return <FaLayerGroup size={12} />;
    }
  },
};

// =============================================================================
// Alert Configuration
// =============================================================================

export const alertSectionConfig: SidebarSectionConfig<Alert> = {
  resourceType: "alert",
  emptyMessage: "Aucune alerte",
  searchPlaceholder: "Rechercher...",
  noResultsMessage: "Aucun résultat",
  supportsFolders: true,
  getGroupIcon: (groupKey: string, groupBy: string) => {
    switch (groupBy) {
      case "type":
        const alertType = Object.entries(ALERT_TYPE_LABELS).find(
          ([, label]) => label === groupKey
        )?.[0] as AlertType | undefined;
        if (alertType && ALERT_TYPE_ICONS[alertType]) {
          return ALERT_TYPE_ICONS[alertType];
        }
        return <FaLayerGroup size={12} />;
      case "status":
        if (groupKey === "Activées") {
          return <FaToggleOn size={12} className="text-green-500" />;
        } else {
          return <FaToggleOff size={12} className="text-gray-400" />;
        }
      default:
        return <FaLayerGroup size={12} />;
    }
  },
};

// =============================================================================
// Dashboard Configuration
// =============================================================================

export const dashboardSectionConfig: SidebarSectionConfig<Dashboard> = {
  resourceType: "dashboard",
  emptyMessage: "Aucun dashboard",
  searchPlaceholder: "Rechercher...",
  noResultsMessage: "Aucun résultat",
  supportsFolders: true,
  getGroupIcon: (groupKey: string, groupBy: string) => {
    switch (groupBy) {
      case "recent":
        return <FaLayerGroup size={12} />;
      case "widgets_count":
        return <FaTh size={12} />;
      default:
        return <FaLayerGroup size={12} />;
    }
  },
};

// =============================================================================
// Widget Configuration
// =============================================================================

export const widgetSectionConfig: SidebarSectionConfig<WidgetWithContext> = {
  resourceType: "widget" as any,
  emptyMessage: "Aucun widget",
  emptyIcon: <FaLayerGroup />,
  emptySubMessage: "Créez des widgets dans vos dashboards",
  searchPlaceholder: "Rechercher...",
  noResultsMessage: "Aucun résultat",
  supportsFolders: false,
  getGroupIcon: (groupKey: string, groupBy: string, widgets: WidgetWithContext[]) => {
    switch (groupBy) {
      case "type":
        if (widgets.length > 0) {
          return <span className="text-xs">{getWidgetIcon(widgets[0].widget.chart)}</span>;
        }
        return <FaLayerGroup size={12} />;
      case "dashboard":
        return <FaTh size={12} />;
      default:
        return <FaLayerGroup size={12} />;
    }
  },
};

// =============================================================================
// Investigation Configuration
// =============================================================================

export const investigationSectionConfig: SidebarSectionConfig<Investigation> = {
  resourceType: "investigation" as any,
  emptyMessage: "Aucune investigation",
  emptyIcon: <FaProjectDiagram />,
  searchPlaceholder: "Rechercher...",
  noResultsMessage: "Aucun résultat",
  supportsFolders: false,
  getGroupIcon: (groupKey: string, groupBy: string) => {
    switch (groupBy) {
      case "status":
        return groupKey === "Actives" ? (
          <FaCircle size={8} className="text-green-500" />
        ) : (
          <FaCircle size={8} className="text-gray-400" />
        );
      default:
        return <FaProjectDiagram size={12} />;
    }
  },
};

// =============================================================================
// Registry
// =============================================================================

export const sidebarSectionConfigs = {
  dashboard: dashboardSectionConfig,
  tracker: trackerSectionConfig,
  enrichment: enrichmentSectionConfig,
  alert: alertSectionConfig,
  widget: widgetSectionConfig,
  investigation: investigationSectionConfig,
} as const;

export type SidebarSectionType = keyof typeof sidebarSectionConfigs;

/**
 * Get the sidebar section configuration for a resource type
 */
export function getSidebarSectionConfig<T>(
  resourceType: SidebarSectionType
): SidebarSectionConfig<T> {
  return sidebarSectionConfigs[resourceType] as SidebarSectionConfig<T>;
}
